import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface ProductReview {
  id: string;
  product_id: string;
  reviewer_name: string;
  rating: number;
  review_text: string;
  image_url: string | null;
  is_verified: boolean;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export const useProductReviews = (productId: string) => {
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Supabase error:', fetchError);
        throw fetchError;
      }

      setReviews(data || []);
    } catch (err) {
      console.error('Error fetching product reviews:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  // Function to refresh reviews (useful after submitting a new review)
  const refreshReviews = () => {
    fetchReviews();
  };

  return { reviews, loading, error, refreshReviews };
};

// Hook to get review statistics
export const useReviewStats = (productId: string) => {
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: [0, 0, 0, 0, 0] // Index 0 = 1 star, Index 4 = 5 stars
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('product_reviews')
          .select('rating')
          .eq('product_id', productId)
          .eq('status', 'approved');

        if (fetchError) {
          throw fetchError;
        }

        if (data && data.length > 0) {
          const totalReviews = data.length;
          const totalRating = data.reduce((sum, review) => sum + review.rating, 0);
          const averageRating = totalRating / totalReviews;

          // Calculate rating distribution
          const distribution = [0, 0, 0, 0, 0];
          data.forEach(review => {
            if (review.rating >= 1 && review.rating <= 5) {
              distribution[review.rating - 1]++;
            }
          });

          setStats({
            averageRating,
            totalReviews,
            ratingDistribution: distribution
          });
        } else {
          setStats({
            averageRating: 0,
            totalReviews: 0,
            ratingDistribution: [0, 0, 0, 0, 0]
          });
        }
      } catch (err) {
        console.error('Error fetching review stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch review stats');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchStats();
    }
  }, [productId]);

  return { stats, loading, error };
};